import { useTextInput } from '@hooks';
import {
  createEmailAccount,
  isEmailVerified,
  useFirebaseAuthUserContext,
} from '@libraries/firebase';
import { Button, FormError, FormRow, TextInput, getLogInRoute } from '@shared';
import { getRedirectPath } from '@utils';
import { useRouter } from 'next/router';
import { FormEvent, useState } from 'react';
import { FacebookButton } from '../components/FacebookButton';
import { FacebookSeparator } from '../components/FacebookSeparator';
import { ForgotPassword } from '../components/ForgotPassword';
import { AuthForm } from '../components/Form';
import { AuthHeading } from '../components/Heading';
import { IntroMessage } from '../components/IntroMessage';
import { SignUpSignInText } from '../components/SignUpSignInText';
import { VerifyEmailMessage } from '../components/VerifyEmailMessage';

export function CreateAccountForm() {
  const [isAuthenticating, setAuthenticating] = useState(false);
  const { user } = useFirebaseAuthUserContext();
  const email = useTextInput();
  const password = useTextInput();
  const router = useRouter();
  const redirectTo = getRedirectPath(router);
  const logInRoute = getLogInRoute(redirectTo);

  // Display email verification message.
  if (user && !isEmailVerified(user)) {
    return <VerifyEmailMessage redirectTo={redirectTo} user={user} />;
  }

  function handleError(newEmailErr: string, newPasswordErr?: string) {
    setAuthenticating(() => false);

    if (newEmailErr) {
      email.updateError(newEmailErr);
      password.updateError('');
    }

    if (newPasswordErr) {
      email.updateError('');
      password.updateError(newPasswordErr);
    }
  }

  function handleAccountCreated() {
    setAuthenticating(() => false);
  }

  function handleCreateAccount(event?: FormEvent) {
    event?.preventDefault();

    email.updateError('');
    password.updateError('');
    setAuthenticating(() => true);
    createEmailAccount(
      email.value,
      password.value,
      redirectTo,
      handleError,
      handleAccountCreated,
    );
  }

  return (
    <AuthForm onSubmit={handleCreateAccount}>
      <AuthHeading>Create Your Account</AuthHeading>

      <IntroMessage>
        Welcome to CNA Classes For Me. Search for schools around you, or add
        your own to the directory!
      </IntroMessage>

      <FormRow>
        <TextInput
          disabled={isAuthenticating}
          hasError={!!email.error}
          onChange={email.updateValue}
          placeholder="Email"
          type="email"
          value={email.value}
        />
        <FormError msg={email.error} />
      </FormRow>

      <FormRow>
        <TextInput
          disabled={isAuthenticating}
          hasError={!!password.error}
          onChange={password.updateValue}
          placeholder="Password"
          type="password"
          value={password.value}
        />
        <FormError msg={password.error} />
      </FormRow>

      <ForgotPassword
        href={logInRoute}
        text="Already have an account? Click here to sign in instead."
      />

      <FormRow>
        <Button onClick={handleCreateAccount}>Create</Button>
      </FormRow>

      <FacebookSeparator />

      <FacebookButton text="Sign in" />

      <SignUpSignInText hasAccount redirectTo={redirectTo} />
    </AuthForm>
  );
}
